/**
 * UniversalFileViewer
 *
 * In-dialog renderer for file content. Picks a viewer based on file type
 * (image / PDF / video / audio / CSV / text / Excel / Word / PowerPoint)
 * and falls back to an iframe + download button for anything else.
 *
 * Auth: every fetch is done via the token in localStorage and converted
 * to a blob: URL so the JWT never appears in any URL the iframe / <img> /
 * <video> / <audio> sees.
 *
 * PDFs are the exception: they are fetched as raw bytes and painted with
 * pdf.js → `<canvas>` via the shared `PdfReader` — NEVER a `blob:` `<iframe>`.
 * The Tauri desktop shells have no native PDF plugin for a `blob:`/`data:`
 * frame (WebView2 shows a broken-document placeholder, WKWebView can hang), so
 * the old blob iframe — fine on web — broke the desktop file manager. This is
 * the SAME reader the Documentation page + the quote/invoice/lead-brief/
 * customer-attachment previews use. See docs/crm.md §11 + memory
 * xop_edge_staff_pdf_iframe_hangs_wkwebview.
 *
 * Lifted out of `app/(authenticated)/file-manager/page.tsx` verbatim — the
 * original file-type predicates were replaced with shared helpers from
 * `@/lib/file-type-utils`; rendering logic, side effects, and the
 * (intentional) PPTX-branch structure are otherwise preserved.
 */

import { useState, useEffect, useCallback } from "react"
import { Box, Button, CircularProgress, Typography } from "@mui/material"
import { FileText, Download, Eye } from "lucide-react"

import { PdfReader } from "@/components/pdf/pdf-reader"

// React document viewers
// @ts-ignore
import * as XLSX from "xlsx"
import mammoth from "mammoth"
// @ts-ignore
import { parse as parsePptx } from "pptxtojson"

import { type FileItem } from "@crm-src/lib/files-api"
import api, { crmApiUrl } from "@crm-src/lib/api"
import { buildSlideElements } from "./pptx-slide"
import {
  isImageFile,
  isPdfFile,
  isVideoFile,
  isAudioFile,
  isCsvFile,
  isTextFile,
  isExcelFile,
  isDocxFile,
  isDocFile,
  isWordFile,
  isPowerPointFile,
  isPptFile,
  isPptxFile,
} from "@crm-src/lib/file-type-utils"

interface UniversalFileViewerProps {
  file: FileItem
  onError: (error: string) => void
  onDownload: () => void
}

export const UniversalFileViewer: React.FC<UniversalFileViewerProps> = ({ file, onError, onDownload }) => {
  const [loading, setLoading] = useState(false)
  const [content, setContent] = useState<string | null>(null)
  const [csvData, setCsvData] = useState<string[][] | null>(null)
  const [excelData, setExcelData] = useState<any>(null)
  const [wordContent, setWordContent] = useState<string>('')
  // PDF bytes for the pdf.js → <canvas> renderer (PdfReader), NOT a blob:
  // <iframe>. See the PDF render branch below.
  const [pdfBytes, setPdfBytes] = useState<ArrayBuffer | null>(null)
  const [pptxData, setPptxData] = useState<any>(null)
  const [currentSlide, setCurrentSlide] = useState<number>(0)
  const [totalSlides, setTotalSlides] = useState<number>(0)

  // Blob URLs created for the current file. We swap raw API URLs (which would
  // require leaking the JWT in the query string) for blob: URLs so the file
  // is fetched via fetch() with an Authorization header instead.
  const [viewBlobUrl, setViewBlobUrl] = useState<string>('')

  // Build the API URL for a file endpoint - WITHOUT token in query string.
  // The auth token is sent via Authorization header in the fetch below.
  const buildFileUrl = (fileId: string, endpoint: 'view' | 'download' = 'view') => {
    return crmApiUrl(`/api/v1/files/${fileId}/${endpoint}`)
  }

  // Fetch a file with bearer auth and return a blob URL safe to use as
  // <iframe src>, <img src>, etc. Does NOT leak the token in the URL.
  const fetchAuthedBlobUrl = useCallback(async (fileId: string, endpoint: 'view' | 'download' = 'view'): Promise<string> => {
    const token = typeof window !== 'undefined' ? localStorage.getItem('xop_crm_token') : null
    const resp = await fetch(buildFileUrl(fileId, endpoint), {
      credentials: 'include',
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    })
    if (!resp.ok) throw new Error(`Failed to load file (${resp.status})`)
    const blob = await resp.blob()
    return URL.createObjectURL(blob)
  }, [])

  // Backwards-compat alias used throughout this component. NEW callers should
  // prefer the blob-based viewBlobUrl when embedding in <iframe>/<img>/<video>.
  const getAuthenticatedUrl = (fileId: string, endpoint: 'view' | 'download' = 'view') => {
    return buildFileUrl(fileId, endpoint)
  }

  // Load a viewable blob URL for media/iframe-rendered files. Cleans up the
  // previous blob URL when the file changes or the component unmounts.
  useEffect(() => {
    let active = true
    let createdUrl: string | null = null

    const load = async () => {
      // PDFs render from raw bytes via PdfReader (see loadDocumentContent), so
      // a blob: URL would be fetched-but-unused — skip it (also mirrors the
      // customer-attachment viewer).
      if (isPdfFile(file)) return
      try {
        const url = await fetchAuthedBlobUrl(file.id, 'view')
        if (!active) {
          URL.revokeObjectURL(url)
          return
        }
        createdUrl = url
        setViewBlobUrl(url)
      } catch (e) {
        if (active) onError('Failed to load file')
      }
    }

    load()

    return () => {
      active = false
      if (createdUrl) URL.revokeObjectURL(createdUrl)
    }
  }, [file.id, fetchAuthedBlobUrl, onError])

  // Load text content for text files and CSV using authenticated API
  const loadTextContent = useCallback(async () => {
    if (!isTextFile(file) && !isCsvFile(file)) return

    try {
      setLoading(true)
      // Use the existing API method that handles authentication properly
      const response = await api.get(`/files/${file.id}/view`, {
        responseType: 'text'
      })
      const text = response.data

      if (isCsvFile(file)) {
        // Parse CSV content
        const rows = text.split('\n').map((row: string) => row.split(',').map((cell: string) => cell.trim().replace(/^"/, '').replace(/"$/, '')))
        setCsvData(rows.filter((row: string[]) => row.some((cell: string) => cell.length > 0)))
      } else {
        setContent(text)
      }
    } catch (error) {
      console.error('Error loading text content:', error)
      onError('Failed to load file content')
    } finally {
      setLoading(false)
    }
  }, [file.id])

  // Load document content using React libraries
  const loadDocumentContent = useCallback(async () => {
    try {
      setLoading(true)
      const dlToken = typeof window !== 'undefined' ? localStorage.getItem('xop_crm_token') : null
      const response = await fetch(buildFileUrl(file.id, 'download'), {
        credentials: 'include',
        headers: dlToken ? { Authorization: `Bearer ${dlToken}` } : {}
      })

      if (!response.ok) {
        throw new Error('Failed to fetch file')
      }

      if (isExcelFile(file)) {
        const arrayBuffer = await response.arrayBuffer()
        try {
          const workbook = XLSX.read(arrayBuffer, { type: 'array' })
          const sheetName = workbook.SheetNames[0]
          const worksheet = workbook.Sheets[sheetName]
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 })

          setExcelData({
            rows: jsonData as any[][]
          })
        } catch (err) {
          console.error('Excel parse error:', err)
          onError('Failed to parse Excel file')
        }
      } else if (isDocxFile(file)) {
        const arrayBuffer = await response.arrayBuffer()
        const result = await mammoth.extractRawText({ arrayBuffer })
        setWordContent(result.value)
      } else if (isPdfFile(file)) {
        // Hand the raw bytes to PdfReader (pdf.js → <canvas>), never a blob:
        // <iframe>. PdfReader copies the buffer internally (toPdfFileData)
        // before pdf.js detaches it, so these source bytes survive a re-render.
        const arrayBuffer = await response.arrayBuffer()
        setPdfBytes(arrayBuffer)
      }
    } catch (error) {
      console.error('Error loading document content:', error)
      onError('Failed to load file content')
    } finally {
      setLoading(false)
    }
  }, [file.id, file.name])

  useEffect(() => {
    if (isTextFile(file) || isCsvFile(file)) {
      loadTextContent()
    } else if (isExcelFile(file) || isDocxFile(file) || isPdfFile(file)) {
      loadDocumentContent()
    }
  }, [file.id, loadTextContent, loadDocumentContent])

  // PPTX processing — modern PowerPoint files are parsed in-browser via
  // pptxtojson. This MUST be a TOP-LEVEL hook: it previously lived inside the
  // `if (isPptxFile(file))` render branch (below), which made it a CONDITIONAL
  // hook — called only for PPTX files, and only after the image/pdf/.../docx
  // early returns above. That violated react-hooks/rules-of-hooks and would
  // crash React with "Rendered more hooks than during the previous render"
  // when the viewer switched between a PPTX file and any other file type.
  // Guard the body by file type instead of guarding the hook call.
  useEffect(() => {
    if (!isPptxFile(file)) return

    // Reset PowerPoint data when file changes
    setPptxData(null)
    setCurrentSlide(0)
    setTotalSlides(0)

    if (!loading) {
      const processPowerPointFile = async () => {
        try {
          setLoading(true)

          const pptxToken = typeof window !== 'undefined' ? localStorage.getItem('xop_crm_token') : null
          const response = await fetch(buildFileUrl(file.id, 'view'), {
            credentials: 'include',
            headers: pptxToken ? { Authorization: `Bearer ${pptxToken}` } : {}
          })
          if (!response.ok) {
            throw new Error('Failed to fetch PPTX file')
          }

          const arrayBuffer = await response.arrayBuffer()

          // Use pptxtojson to parse the PPTX file
          const pptxResult = await parsePptx(arrayBuffer)

          setPptxData(pptxResult)
          setCurrentSlide(0)
          // pptxtojson may return different structure, handle various cases
          const result = pptxResult as any
          const slideCount = result?.slides?.length ||
                            result?.length ||
                            (Array.isArray(result) ? result.length : 0) ||
                            (result?.slidePages ? result.slidePages.length : 0) ||
                            0
          setTotalSlides(slideCount)

          setLoading(false)
        } catch (err) {
          console.error('PPTX processing failed:', err)
          setLoading(false)
          onError?.(`Failed to load PPTX presentation: ${err}`)
        }
      }

      processPowerPointFile()
    }
    // Only depend on file.id to prevent an infinite reprocess loop (the body
    // reads `loading`/`onError` intentionally as a snapshot at file-change).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file.id])

  // Image Viewer - uses blob URL so the JWT is never in the request URL.
  if (isImageFile(file)) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "surfaceSoft", borderRadius: 1, minHeight: 400 }}>
        {viewBlobUrl ? (
          <Box
            component="img"
            src={viewBlobUrl}
            alt={file.name}
            onError={() => onError('Failed to load image')}
            sx={{ maxWidth: "100%", maxHeight: 600, objectFit: "contain", borderRadius: 1, boxShadow: 3 }}
          />
        ) : (
          <Typography variant="body2" sx={{ color: "text.secondary" }}>Loading...</Typography>
        )}
      </Box>
    )
  }

  // PDF Viewer — pdf.js → <canvas> via the shared PdfReader, NEVER a blob:
  // <iframe>. The Tauri desktop shells have no native PDF plugin for a
  // blob:/data: frame (WebView2 shows a broken-document placeholder, WKWebView
  // can spin/hang), so the old iframe — fine on the web — blanked the desktop
  // file manager. PdfReader paints each page to a <canvas> (identical web +
  // desktop), hands pdf.js a COPY of the bytes (toPdfFileData) so the "Buffer
  // is already detached" crash can't recur, and degrades to a Download-only
  // card on any render failure (the dialog footer keeps the non-hang Download).
  // Same renderer as the Documentation reader + the customer-attachment /
  // quote / invoice / lead-brief previews. See docs/crm.md §11 + memory
  // xop_edge_staff_pdf_iframe_hangs_wkwebview.
  if (isPdfFile(file)) {
    if (loading || !pdfBytes) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: 800, bgcolor: "surfaceSoft", borderRadius: 1, border: 1, borderColor: "divider" }}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>Loading...</Typography>
        </Box>
      )
    }
    return (
      <PdfReader
        key={file.id}
        bytes={pdfBytes}
        filename={file.name}
        containerTestId="file-pdf-preview"
        fallbackTestId="file-pdf-fallback"
        fallbackText="This PDF couldn’t be previewed inline. Use Download to view it."
        maxHeight={800}
      />
    )
  }

  // Video Player
  if (isVideoFile(file)) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "common.black", borderRadius: 1 }}>
        {viewBlobUrl ? (
          <Box
            component="video"
            src={viewBlobUrl}
            controls
            onError={() => onError('Failed to load video')}
            sx={{ maxWidth: "100%", maxHeight: 600, borderRadius: 1 }}
          >
            Your browser does not support video playback.
          </Box>
        ) : (
          <Typography variant="body2" sx={{ color: "text.secondary", p: 4 }}>Loading...</Typography>
        )}
      </Box>
    )
  }

  // Audio Player
  if (isAudioFile(file)) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "surfaceSoft", borderRadius: 1, minHeight: 200 }}>
        <Box sx={{ textAlign: "center", display: "flex", flexDirection: "column", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: 80, height: 80, bgcolor: (theme) => theme.palette.mode === "light" ? "#dbeafe" : "#1e3a5f", borderRadius: "50%", mx: "auto", color: "primary.main" }}>
            <FileText size={32} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 500, fontSize: "1.125rem" }}>{file.name}</Typography>
            {viewBlobUrl ? (
              <Box
                component="audio"
                src={viewBlobUrl}
                controls
                onError={() => onError('Failed to load audio')}
                sx={{ mt: 2 }}
              >
                Your browser does not support audio playback.
              </Box>
            ) : (
              <Typography variant="body2" sx={{ color: "text.secondary" }}>Loading...</Typography>
            )}
          </Box>
        </Box>
      </Box>
    )
  }

  // CSV Viewer
  if (isCsvFile(file)) {
    if (loading) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 6 }}>
          <CircularProgress size={32} sx={{ mr: 1 }} />
          <span>Loading CSV...</span>
        </Box>
      )
    }

    if (csvData && csvData.length > 0) {
      return (
        <Box sx={{ bgcolor: "background.paper", borderRadius: 1, border: 1, borderColor: "divider", overflow: "hidden" }}>
          <Box sx={{ overflow: "auto", maxHeight: 600 }}>
            <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
              <Box component="thead" sx={{ bgcolor: "surfaceSoft", position: "sticky", top: 0 }}>
                <tr>
                  {csvData[0].map((header, index) => (
                    <Box
                      component="th"
                      key={index}
                      sx={{ border: 1, borderColor: "divider", px: 2, py: 1, textAlign: "left", fontSize: "0.875rem", fontWeight: 500, color: "text.primary" }}
                    >
                      {header}
                    </Box>
                  ))}
                </tr>
              </Box>
              <tbody>
                {csvData.slice(1).map((row, rowIndex) => (
                  <Box component="tr" key={rowIndex} sx={{ bgcolor: rowIndex % 2 === 0 ? "background.paper" : "surfaceSoft" }}>
                    {row.map((cell, cellIndex) => (
                      <Box
                        component="td"
                        key={cellIndex}
                        sx={{ border: 1, borderColor: "divider", px: 2, py: 1, fontSize: "0.875rem", color: "text.secondary" }}
                      >
                        {cell}
                      </Box>
                    ))}
                  </Box>
                ))}
              </tbody>
            </Box>
          </Box>
          <Box sx={{ px: 2, py: 1, bgcolor: "surfaceSoft", borderTop: 1, borderColor: "divider", fontSize: "0.875rem", color: "text.secondary" }}>
            {csvData.length - 1} rows, {csvData[0].length} columns
          </Box>
        </Box>
      )
    }
  }

  // Text File Viewer
  if (isTextFile(file)) {
    if (loading) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 6 }}>
          <CircularProgress size={32} sx={{ mr: 1 }} />
          <span>Loading text...</span>
        </Box>
      )
    }

    if (content !== null) {
      return (
        <Box sx={{ bgcolor: "surfaceSoft", borderRadius: 1, p: 2 }}>
          <Box sx={{ bgcolor: "background.paper", borderRadius: 1, border: 1, borderColor: "divider", p: 2, fontFamily: "monospace", fontSize: "0.875rem", overflow: "auto", maxHeight: 600, whiteSpace: "pre-wrap" }}>
            {content}
          </Box>
        </Box>
      )
    }
  }

  // Excel Viewer using react-excel-renderer
  if (isExcelFile(file)) {
    if (loading) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", p: 4 }}>
          <CircularProgress size={32} sx={{ color: "text.secondary" }} />
          <Typography sx={{ ml: 1, color: "text.secondary" }}>Loading Excel file...</Typography>
        </Box>
      )
    }

    if (!excelData || !excelData.rows) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", p: 4 }}>
          <Typography sx={{ color: "text.secondary" }}>No Excel data to display</Typography>
        </Box>
      )
    }

    return (
      <Box sx={{ width: "100%", overflow: "auto", maxHeight: 700, bgcolor: "background.paper", borderRadius: 1, border: 1, borderColor: "divider" }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider", bgcolor: "surfaceSoft" }}>
          <Typography sx={{ fontWeight: 500 }}>Excel Spreadsheet: {file.name}</Typography>
        </Box>
        <Box component="table" sx={{ width: "100%", fontSize: "0.875rem" }}>
          <tbody>
            {excelData.rows.map((row: any[], rowIndex: number) => (
              <Box
                component="tr"
                key={rowIndex}
                sx={{
                  borderBottom: 1,
                  borderColor: "divider",
                  ...(rowIndex === 0
                    ? { bgcolor: "surfaceSoft", fontWeight: 500 }
                    : { "&:hover": { bgcolor: "surfaceSoft" } }),
                }}
              >
                {row.map((cell: any, cellIndex: number) => (
                  <Box component="td" key={cellIndex} sx={{ p: 1.5, color: "text.secondary", borderRight: 1, borderColor: "divider" }}>
                    {cell || ''}
                  </Box>
                ))}
              </Box>
            ))}
          </tbody>
        </Box>
      </Box>
    )
  }

  // Word Document Files - Handle both DOC and DOCX formats
  if (isWordFile(file)) {
    // Legacy DOC files (binary format) - can't be parsed well in browser
    if (isDocFile(file)) {
      return (
        <Box sx={{ width: "100%", height: 700, background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)", borderRadius: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Box sx={{ textAlign: "center", display: "flex", flexDirection: "column", gap: 2, p: 4, maxWidth: 512 }}>
            {/* Word Icon */}
            <Box sx={{ width: 96, height: 96, bgcolor: "background.paper", borderRadius: 1, boxShadow: 3, display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", color: "primary.main" }}>
              <FileText size={48} />
            </Box>

            {/* Simple Message */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Typography sx={{ fontSize: "1.5rem", fontWeight: 700, color: "text.primary" }}>Legacy Word File (DOC)</Typography>
              <Typography sx={{ fontSize: "1rem", color: "text.secondary", lineHeight: 1.6 }}>
                This is an older Word format that can't be displayed in the browser.
                Please convert to DOCX format or download to view.
              </Typography>
            </Box>
          </Box>
        </Box>
      )
    }

    // DOCX files - can be processed with mammoth
    if (isDocxFile(file)) {
      if (loading) {
        return (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", p: 4 }}>
            <CircularProgress size={32} sx={{ color: "text.secondary" }} />
            <Typography sx={{ ml: 1, color: "text.secondary" }}>Loading Word document...</Typography>
          </Box>
        )
      }

      return (
        <Box sx={{ width: "100%", height: 700, bgcolor: "background.paper", borderRadius: 1, border: 1, borderColor: "divider", p: 3, overflow: "auto" }}>
          <Box sx={{ mb: 2, pb: 2, borderBottom: 1, borderColor: "divider" }}>
            <Typography sx={{ fontWeight: 500, fontSize: "1.125rem" }}>Word Document: {file.name}</Typography>
          </Box>
          <Box sx={{ maxWidth: "none" }}>
            <Box component="pre" sx={{ whiteSpace: "pre-wrap", fontSize: "0.875rem", color: "text.primary", fontFamily: (theme) => theme.typography.fontFamily, lineHeight: 1.6 }}>
              {wordContent}
            </Box>
          </Box>
        </Box>
      )
    }
  }

  // PowerPoint Files - Handle both PPT and PPTX formats
  if (isPowerPointFile(file)) {
    // Legacy PPT files (binary format) - can't be parsed in browser
    if (isPptFile(file)) {
      return (
        <Box sx={{ width: "100%", height: 700, background: "linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)", borderRadius: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Box sx={{ textAlign: "center", display: "flex", flexDirection: "column", gap: 2, p: 4, maxWidth: 512 }}>
            {/* PowerPoint Icon */}
            <Box sx={{ width: 96, height: 96, bgcolor: "background.paper", borderRadius: 1, boxShadow: 3, display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", color: "#ea580c" }}>
              <FileText size={48} />
            </Box>

            {/* Simple Message */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Typography sx={{ fontSize: "1.5rem", fontWeight: 700, color: "text.primary" }}>Legacy PowerPoint File (PPT)</Typography>
              <Typography sx={{ fontSize: "1rem", color: "text.secondary", lineHeight: 1.6 }}>
                This is an older PowerPoint format that can't be displayed in the browser.
                Please convert to PPTX format or download to view.
              </Typography>
            </Box>
          </Box>
        </Box>
      )
    }

    // PPTX Files - JavaScript-based viewer for modern format. The PPTX
    // *processing* effect is hoisted to the TOP of this component (see the
    // isPptxFile-guarded useEffect above) so it is never a conditional hook.
    if (isPptxFile(file)) {

    if (loading) {
      return (
        <Box sx={{ width: "100%", height: 700, background: "linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)", borderRadius: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Box sx={{ textAlign: "center", display: "flex", flexDirection: "column", gap: 2 }}>
            <CircularProgress size={48} sx={{ color: "#ea580c", mx: "auto" }} />
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography sx={{ fontSize: "1.125rem", fontWeight: 600, color: "text.primary" }}>Loading PowerPoint Presentation</Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>Parsing slides and content...</Typography>
            </Box>
          </Box>
        </Box>
      )
    }

    if (pptxData && totalSlides > 0) {
      // Handle different possible data structures from pptxtojson
      let currentSlideData
      if (pptxData.slides) {
        currentSlideData = pptxData.slides[currentSlide]
      } else if (Array.isArray(pptxData)) {
        currentSlideData = pptxData[currentSlide]
      } else if (pptxData.slidePages) {
        currentSlideData = pptxData.slidePages[currentSlide]
      } else {
        currentSlideData = pptxData
      }

      return (
        <Box sx={{ width: "100%", height: 700, bgcolor: "background.paper", borderRadius: 1, border: 1, borderColor: "divider", display: "flex", flexDirection: "column" }}>
          {/* Slide Content Area */}
          <Box sx={{ flex: 1, bgcolor: "surfaceSoft", p: 4, overflow: "auto" }}>
            <Box sx={{ bgcolor: "background.paper", borderRadius: 1, boxShadow: 3, p: 4, maxWidth: 896, mx: "auto", minHeight: 500 }}>
              {currentSlideData ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  {/* Slide Title */}
                  {currentSlideData.title && (
                    <Box sx={{ borderBottom: 1, borderColor: "divider", pb: 2 }}>
                      <Typography component="h2" sx={{ fontSize: "1.5rem", fontWeight: 700, color: "text.primary" }}>{currentSlideData.title}</Typography>
                    </Box>
                  )}

                  {/* Visual Slide Renderer - Actual PowerPoint-like Display */}
                  <Box sx={{ width: "100%", height: "100%" }}>
                    {(() => {
                      const elements = buildSlideElements(currentSlideData)

                      return (
                        <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
                          {/* PowerPoint Slide Canvas - Realistic Presentation View */}
                          <Box
                            sx={{ position: "relative", width: "100%", bgcolor: "background.paper", boxShadow: 6, border: 1, borderColor: "divider" }}
                            style={{
                              aspectRatio: '16/9', // Standard PowerPoint aspect ratio
                              maxHeight: '70vh',
                              height: '70vh',
                              margin: '0 auto',
                              borderRadius: '12px',
                              overflow: 'hidden',
                              boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                              background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
                            }}
                          >
                            {/* Slide Content - Layered Rendering */}
                            {elements.length > 0 ? (
                              <Box sx={{ position: "relative", width: "100%", height: "100%", overflow: "hidden" }}>
                                {/* Render elements in proper z-index order */}
                                {elements.map((element, index) => (
                                  <Box
                                    key={element.id}
                                    sx={{ position: "absolute" }}
                                    style={{
                                      left: element.position.x,
                                      top: element.position.y,
                                      width: element.position.width,
                                      height: element.position.height,
                                      zIndex: element.position.zIndex || (element.type === 'image' ? 1 : 5)
                                    }}
                                  >
                                    {element.type === 'image' && (
                                      <Box
                                        component="img"
                                        src={element.content}
                                        alt={`Slide ${currentSlide + 1} - Image ${index + 1}`}
                                        sx={{ width: "100%", height: "100%", ...(element.position.zIndex === 1 ? {} : { borderRadius: 1, boxShadow: 2 }) }}
                                        style={{
                                          objectFit: element.style.objectFit || 'contain',
                                          borderRadius: element.style.borderRadius || '0',
                                          filter: element.position.zIndex === 1 ? 'brightness(0.9)' : 'none',
                                          border: element.position.zIndex === 1 ? 'none' : '1px solid rgba(0,0,0,0.1)'
                                        }}
                                        onError={(e) => {
                                          (e.target as HTMLElement).style.display = 'none'
                                        }}
                                      />
                                    )}
                                    {element.type === 'text' && (
                                      <Box
                                        sx={{ userSelect: "text", width: "100%" }}
                                        style={{
                                          ...element.style,
                                          padding: (element.priority || 999) === 1 ? '8px 12px' : '4px 8px',
                                          backgroundColor: (element.priority || 999) === 1 ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.8)',
                                          borderRadius: (element.priority || 999) === 1 ? '8px' : '4px',
                                          backdropFilter: 'blur(2px)',
                                          boxShadow: (element.priority || 999) === 1 ? '0 2px 8px rgba(0,0,0,0.15)' : '0 1px 4px rgba(0,0,0,0.1)',
                                          border: '1px solid rgba(255,255,255,0.3)'
                                        }}
                                      >
                                        {element.content}
                                      </Box>
                                    )}
                                  </Box>
                                ))}
                              </Box>
                            ) : (
                              /* Fallback for slides that can't be rendered visually */
                              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", background: "linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)" }}>
                                <Box sx={{ textAlign: "center", p: 4 }}>
                                  <Box sx={{ width: 64, height: 64, bgcolor: "#fed7aa", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 2, color: "#ea580c" }}>
                                    <FileText size={32} />
                                  </Box>
                                  <Typography sx={{ fontSize: "1.25rem", fontWeight: 600, color: "text.primary", mb: 1 }}>
                                    Slide {currentSlide + 1}
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
                                    Complex slide layout with animations or special formatting
                                  </Typography>
                                  <Box sx={{ bgcolor: "rgba(255,255,255,0.8)", borderRadius: 1, p: 2, boxShadow: 1 }}>
                                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                      💡 Download the presentation to view this slide with full PowerPoint features
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>
                            )}
                          </Box>

                        </Box>
                      )
                    })()}
                  </Box>
                </Box>
              ) : (
                <Box sx={{ textAlign: "center", py: 6 }}>
                  <Box sx={{ display: "flex", justifyContent: "center", mb: 2, color: "text.disabled" }}>
                    <FileText size={64} />
                  </Box>
                  <Typography sx={{ fontSize: "1.125rem", fontWeight: 600, color: "text.primary", mb: 1 }}>Slide {currentSlide + 1}</Typography>
                  <Typography sx={{ color: "text.secondary" }}>Unable to parse slide content</Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* Navigation Footer */}
          <Box sx={{ bgcolor: "surfaceSoft", p: 2, borderBottomLeftRadius: 8, borderBottomRightRadius: 8, borderTop: 1, borderColor: "divider" }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 2 }}>
              <Button
                onClick={() => setCurrentSlide(Math.max(0, currentSlide - 1))}
                disabled={currentSlide === 0}
                variant="outlined"
                color="inherit"
                size="small"
              >
                ← Previous
              </Button>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {Array.from({ length: Math.min(totalSlides, 10) }, (_, i) => (
                  <Box
                    component="button"
                    key={i}
                    onClick={() => setCurrentSlide(i)}
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      border: "none",
                      cursor: "pointer",
                      p: 0,
                      transition: "background-color .2s",
                      ...(i === currentSlide
                        ? { bgcolor: "#ea580c" }
                        : { bgcolor: "grey.300", "&:hover": { bgcolor: "grey.400" } }),
                    }}
                  />
                ))}
                {totalSlides > 10 && <Typography component="span" sx={{ color: "text.secondary" }}>...</Typography>}
              </Box>

              <Button
                onClick={() => setCurrentSlide(Math.min(totalSlides - 1, currentSlide + 1))}
                disabled={currentSlide === totalSlides - 1}
                variant="outlined"
                color="inherit"
                size="small"
              >
                Next →
              </Button>
            </Box>
          </Box>
        </Box>
      )
    }

    // Fallback if parsing failed
    return (
      <Box sx={{ width: "100%", height: 700, background: "linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)", borderRadius: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Box sx={{ textAlign: "center", display: "flex", flexDirection: "column", gap: 3, p: 4, maxWidth: 512 }}>
          <Box sx={{ width: 96, height: 96, bgcolor: "background.paper", borderRadius: 1, boxShadow: 3, display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", color: "#ea580c" }}>
            <FileText size={48} />
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Typography sx={{ fontSize: "1.25rem", fontWeight: 700, color: "text.primary" }}>{file.name}</Typography>
            <Typography sx={{ color: "text.secondary" }}>Unable to parse PowerPoint presentation</Typography>
          </Box>
          <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1.5, justifyContent: "center" }}>
            <Button
              onClick={onDownload}
              variant="contained"
              startIcon={<Download size={16} />}
              sx={{ bgcolor: "#ea580c", "&:hover": { bgcolor: "#c2410c" }, color: "#fff" }}
            >
              Download File
            </Button>
            <Button
              onClick={async () => {
                try {
                  const url = await fetchAuthedBlobUrl(file.id, 'view')
                  window.open(url, '_blank')
                } catch {
                  onError('Failed to open file in browser')
                }
              }}
              variant="outlined"
              color="inherit"
              startIcon={<Eye size={16} />}
            >
              Try Browser View
            </Button>
          </Box>
        </Box>
      </Box>
    )
  }
}

  // Fallback for unsupported files - Try to show in iframe first
  return (
    <Box sx={{ width: "100%", height: 700, bgcolor: "surfaceSoft", borderRadius: 1, position: "relative" }}>
      {viewBlobUrl ? (
        <Box
          component="iframe"
          src={viewBlobUrl}
          title={`File Viewer - ${file.name}`}
          onError={() => {
            // If iframe fails, show download option
            onError('File cannot be previewed in browser')
          }}
          sx={{ width: "100%", height: "100%", borderRadius: 1, border: 0 }}
        />
      ) : (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>Loading...</Typography>
        </Box>
      )}
      {/* Fallback download option */}
      <Box sx={{ position: "absolute", bottom: 16, right: 16 }}>
        <Button
          onClick={onDownload}
          size="small"
          startIcon={<Download size={16} />}
          sx={{ bgcolor: "rgba(255,255,255,0.9)", "&:hover": { bgcolor: "#fff" }, color: "text.secondary", boxShadow: 3 }}
        >
          Download
        </Button>
      </Box>
    </Box>
  )
}
